/**
 * The boot-failure card — the last thing standing when `main()` never gets far enough to
 * mount React.
 *
 * A rejected bootstrap (WASM init, IndexedDB blocked, a bad cloud URL) left `#root` exactly
 * as the HTML shipped it: an empty div. The user saw a white window with no text, no error
 * and nothing to send to support, and the failure existed only in a devtools console they
 * were never going to open.
 *
 * Deliberately PLAIN DOM with inline styles: at this point React may be the thing that
 * failed, and the stylesheet may not have loaded either — nothing here may depend on the app.
 */

/** Build the plain-text details blob (what "Copy details" puts on the clipboard). */
function detailsOf(error: unknown, appVersion?: string): string {
  const message = error instanceof Error ? (error.stack ?? error.message) : String(error);
  return [
    "Image Maker Gen Z — startup failure",
    appVersion ? `Version: ${appVersion}` : null,
    `When: ${new Date().toISOString()}`,
    typeof navigator !== "undefined" ? `Agent: ${navigator.userAgent}` : null,
    "",
    message,
  ].filter((l) => l !== null).join("\n");
}

/**
 * Replace the app root with a static failure card. Safe to call from a `.catch()` on
 * `main()`; it never throws (a failure HERE would be the white window all over again).
 */
export function renderBootFailure(error: unknown, opts: { rootId?: string; appVersion?: string } = {}): void {
  try {
    if (typeof document === "undefined") return;
    const root = document.getElementById(opts.rootId ?? "root");
    if (!root) return;
    const details = detailsOf(error, opts.appVersion);
    const message = error instanceof Error ? error.message : String(error);

    root.textContent = "";
    const card = document.createElement("div");
    card.setAttribute("role", "alert");
    card.setAttribute("data-testid", "boot-failure");
    card.style.cssText =
      "max-width:520px;margin:12vh auto;padding:24px;border:1px solid #d8d8d8;border-radius:12px;" +
      "font:14px/1.55 system-ui,-apple-system,Segoe UI,sans-serif;color:#1a1a1a;background:#fff;";

    const title = document.createElement("strong");
    title.textContent = "Image Maker couldn't start";
    title.style.cssText = "display:block;font-size:17px;margin-bottom:8px;";

    const reason = document.createElement("p");
    reason.setAttribute("data-testid", "boot-failure-message");
    reason.textContent = message;
    reason.style.cssText = "margin:0 0 16px;color:#5a5a5a;white-space:pre-wrap;word-break:break-word;";

    const row = document.createElement("div");
    row.style.cssText = "display:flex;gap:8px;";

    const reload = document.createElement("button");
    reload.type = "button";
    reload.textContent = "Reload";
    reload.setAttribute("data-testid", "boot-failure-reload");
    reload.style.cssText = "padding:8px 14px;border-radius:8px;border:1px solid #1a1a1a;background:#1a1a1a;color:#fff;cursor:pointer;";
    reload.onclick = () => { try { window.location.reload(); } catch { /* nothing left to do */ } };

    const copy = document.createElement("button");
    copy.type = "button";
    copy.textContent = "Copy details";
    copy.setAttribute("data-testid", "boot-failure-copy");
    copy.style.cssText = "padding:8px 14px;border-radius:8px;border:1px solid #d8d8d8;background:#fff;color:#1a1a1a;cursor:pointer;";
    copy.onclick = () => {
      // No false success: if the clipboard is unavailable, the text below stays selectable.
      void navigator?.clipboard?.writeText?.(details)
        .then(() => { copy.textContent = "Copied"; })
        .catch(() => { /* the <pre> is still selectable */ });
    };

    // The details are ALWAYS on screen too — a clipboard-less webview must still be able
    // to hand the error to support.
    const pre = document.createElement("pre");
    pre.setAttribute("data-testid", "boot-failure-details");
    pre.textContent = details;
    pre.style.cssText =
      "margin:16px 0 0;padding:12px;max-height:220px;overflow:auto;border-radius:8px;background:#f5f5f5;" +
      "font:12px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;white-space:pre-wrap;word-break:break-word;";

    row.append(reload, copy);
    card.append(title, reason, row, pre);
    root.append(card);
  } catch {
    /* the card itself must never be the reason the window is blank */
  }
}
